/**
 * React Query mutation hooks for manual readings.
 * Handles create, update, and delete operations.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import {
  ManualDMInputResponse,
  manualInputApi,
  prepareReadingForApi,
  processReadingResponse,
} from "./readingsApiHelpers";

/**
 * Create a reading.
 */
export function useCreateReading() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (manualInput: ManualDMInputResponse) => {
      const preparedInput = prepareReadingForApi(manualInput);
      const res = await manualInputApi.createOne(preparedInput);
      const created = await manualInputApi.getOne(res.guid);
      return processReadingResponse(created);
    },
    onSuccess: (data) => {
      const fieldId = data.manualDryMatterInput.field;
      queryClient.setQueryData(
        queryKeys.events.readings.byField(fieldId),
        (old: ManualDMInputResponse[] | undefined) => [...(old || []), data]
      );
    },
  });
}

/**
 * Update a reading.
 */
export function useUpdateReading() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (manualInput: ManualDMInputResponse) => {
      const preparedInput = prepareReadingForApi(manualInput);
      await manualInputApi.updateOne(preparedInput);
      const updated = await manualInputApi.getOne(manualInput.guid);
      return processReadingResponse(updated);
    },
    onSuccess: (data) => {
      const fieldId = data.manualDryMatterInput.field;
      queryClient.setQueryData(
        queryKeys.events.readings.byField(fieldId),
        (old: ManualDMInputResponse[] | undefined) =>
          (old || []).map((r) => (r.guid === data.guid ? data : r))
      );
    },
  });
}

/**
 * Delete a reading.
 */
export function useDeleteReading() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { readingId: string; fieldId: string }) => {
      await manualInputApi.deleteOne(params.readingId);
      return params;
    },
    onSuccess: ({ readingId, fieldId }) => {
      queryClient.setQueryData(
        queryKeys.events.readings.byField(fieldId),
        (old: ManualDMInputResponse[] | undefined) =>
          (old || []).filter((r) => r.guid !== readingId)
      );
    },
  });
}

/**
 * Create multiple readings in bulk.
 */
export function useCreateReadingsBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (manualInputs: Array<ManualDMInputResponse>) => {
      const preparedInputs = manualInputs.map(prepareReadingForApi);

      const res = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/v1/manualDryMatterInput/bulk`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ manualDryMatterInputs: preparedInputs }),
        }
      );
      return res.json() as Promise<unknown>;
    },
    onSuccess: async () => {
      // Invalidate all readings queries to refetch
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.readings.all,
      });
    },
  });
}
